import Link from 'next/link'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { InternalLink } from '../../const/Links'
import { useTranslate } from '../../contexts/TranslationContext'
import type { BrandTabResult } from '../../data/BrandFragment'
import type { ItemSubCategoryPreviewResult } from '../../data/ItemSubCategoryFragment'
import { BrandBanner } from '../shared/BrandBanner'
import { Container } from '../shared/Container'
import { ManufacturerArticle } from '../shared/ManufacturerArticle'
import { TagRow } from '../shared/TagRow'
import style from './ManufacturerTab.module.sass'

type ManufacturerTabProps = {
	brand: BrandTabResult
	flavorList: ItemSubCategoryPreviewResult[]
}

export const ManufacturerTab: FunctionComponent<ManufacturerTabProps> = ({ brand, flavorList }) => {
	const residenceOfManufacturers = brand.manufacturers.filter((manufacturer) => manufacturer.residence)
	const { query } = useRouter()
	const translate = useTranslate()

	return (
		<Container size="narrow" variant="leftStickyAligned" disableGutters>
			<div className={style.branBannerWrapper} id="tab3">
				<BrandBanner
					key={brand.id}
					brandId={brand.id}
					title={brand.name}
					buttonLabel={
						brand.paginateProducts.pageInfo.totalCount === 1
							? `${translate('manufacturerTab.banner.button.single')} (${brand.paginateProducts.pageInfo.totalCount})`
							: `${translate('manufacturerTab.banner.button.multiple')} (${brand.paginateProducts.pageInfo.totalCount})`
					}
					href={{ pathname: `${InternalLink.Brand}${brand.slug}`, query: { ...query } }}
					lead={brand.lead}
					buttonDisabled={brand.paginateProducts.pageInfo.totalCount === 0}
					imageUrl={brand.logo?.url}
					isOnProductDetail
					tagContent={flavorList.length > 0 ? <TagRow tags={flavorList} /> : undefined}
				/>
			</div>
			<ManufacturerArticle
				manufacturer={
					brand.manufacturers.length > 0
						? brand.manufacturers.map((manufacturer) =>
								manufacturer.link ? (
									<span key={manufacturer.id} className={style.separatedBySlash}>
										<Link href={manufacturer.link} className={style.link} target="_blank" title={manufacturer.name}>
											{manufacturer.name}
										</Link>
									</span>
								) : (
									<span key={manufacturer.id} className={style.separatedBySlash}>
										{manufacturer.name}
									</span>
								),
							)
						: undefined
				}
				residence={
					residenceOfManufacturers.length > 0
						? residenceOfManufacturers.map(
								(manufacturer) =>
									manufacturer.residence && (
										<span key={manufacturer.id} className={style.separatedByComma}>
											{manufacturer.residence.name}
										</span>
									),
							)
						: undefined
				}
				countriesOfProduction={
					brand.countriesOfProduction.length > 0
						? brand.countriesOfProduction.map((country) => (
								<span key={country.name} className={style.separatedBySlash}>
									{country.name}
								</span>
							))
						: undefined
				}
				distributor={
					brand.distributor &&
					(brand.distributor?.link ? (
						<span key={brand.distributor.id} className={style.separatedByComma}>
							<Link href={brand.distributor.link} title={brand.distributor.name} className={style.link} target="_blank">
								{brand.distributor.name}
							</Link>
						</span>
					) : (
						<span key={brand.distributor.id} className={style.separatedByComma}>
							{brand.distributor.name}
						</span>
					))
				}
			/>
		</Container>
	)
}
